import { useState, useEffect } from 'react';
import { Container, Card, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { Plus } from 'lucide-react';
import { useRedirect } from '../../../apps/hooks/system/useRedirect';
import { AddRedirectModal, RedirectList } from './components';

export default function RedirectConfigurationPage() {
  const {
    redirectsQuery,
    addRedirectMutation,
    deleteRedirectMutation,
    purgeCacheMutation,
    isInvalidating,
  } = useRedirect();

  const [showAddModal, setShowAddModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const redirects = redirectsQuery.data || [];
  const isLoading = redirectsQuery.isLoading;

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
    return;
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
    return;
  }, [error]);

  const handleAddRedirect = async (sourceUrl: string, targetUrl: string) => {
    try {
      setError(null);
      await addRedirectMutation.mutateAsync({ sourceUrl, targetUrl });
      setSuccess('Redirect rule added successfully!');
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteRedirect = async (sourceUrl: string) => {
    try {
      setError(null);
      await deleteRedirectMutation.mutateAsync({ sourceUrl });
      setSuccess('Redirect rule deleted successfully!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete redirect');
    }
  };

  const handlePurgeCache = async () => {
    try {
      setError(null);
      await purgeCacheMutation.mutateAsync();
      setSuccess('Cache purged successfully!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to purge cache');
    }
  };

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h2>Redirect Configuration</h2>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={() => setShowAddModal(true)} disabled={addRedirectMutation.isPending}>
            <Plus size={16} className="me-2" />
            Add New Rule
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-4" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mb-4" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0">Current Status</h5>
              <small className="text-muted">
                {redirects.length} redirect rule{redirects.length !== 1 ? 's' : ''} configured
              </small>
            </div>
            <Button variant="primary" onClick={handlePurgeCache} disabled={purgeCacheMutation.isPending || isInvalidating}>
              {purgeCacheMutation.isPending ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Purging...
                </>
              ) : isInvalidating ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  In Progress...
                </>
              ) : (
                'Purge Cloudflare Cache'
              )}
            </Button>
          </div>
        </Card.Header>
        {isInvalidating && (
          <Card.Body>
            <Alert variant="warning" className="mb-0">
              <Spinner animation="border" size="sm" className="me-2" />
              Cache invalidation is currently in progress...
            </Alert>
          </Card.Body>
        )}
      </Card>

      <Card>
        <Card.Body>
          <RedirectList
            redirects={redirects}
            onDelete={handleDeleteRedirect}
            isLoading={isLoading}
            isDeleting={deleteRedirectMutation.isPending}
          />
        </Card.Body>
      </Card>

      <AddRedirectModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSubmit={handleAddRedirect}
        existingRedirects={redirects}
        isSubmitting={addRedirectMutation.isPending}
      />
    </Container>
  );
}

